import { LOCAL_ENVIRONMENT } from "../environment";
declare const process: { env: Record<string, string | undefined> };

// Toggles between FastAPI local (8000) and Production API
export const SERVER_BASE_URL = LOCAL_ENVIRONMENT 
    ? 'http://localhost:8000/' 
    : (process.env.API_URL || 'https://api.medcleir.com/');

// Root path segments
const auth = 'auth/';
const agenda = 'agenda/';
const entries = 'entries/';
const schedule = 'schedule/';
const user = 'user/';
const me = 'me/';

// Geography
const geography = 'geography/';
const state = 'state/';
const onboarding = 'onboarding/';

const pro = 'pro/';
const base = 'base/';


const contact = 'contact/';
const qualifications = 'qualifications/';
const specialties = 'specialties/';
const subspecialties = 'subspecialties/';
const skills = 'skills/';
const address = 'address/';
const address_active = 'address_active/';
const business_hours = 'working_hours/';
const odontogram = 'odontogram/';
const whitelist = 'whitelist/';
const medic_assistant = 'medic_assistant/'
const cieConditions = 'cie_conditions/';
const drugs = 'drugs/';
const diagnoses = 'diagnoses/';
const medic = 'medic/';

const health_background = 'health_background/';
const allergies = 'allergies/';
const hereditary = 'hereditary/';
const members = 'members/';
const notes = '/notes';
const reactions = '/reactions';
const conditions = '/conditions';

// Common suffixes
const create_suffix = 'create';
const read_suffix = 'read';
const all_suffix = 'all';
const update_suffix = 'update';
const search_suffix = 'search/';
const upgrade_suffix = 'upgrade';
const add_one = '/add_one';

// Common parameters
const query_suffix = '/?query=';

// Specific nested paths (RELATIVE)
const general_info = user + 'general_info/';
const prescriptions = user + 'prescriptions/';
const full_name = 'full_name/';

// Final Route Map
const API_ROUTES = {
    // Auth & Register
    login: `${auth}login`,
    logout: `${auth}logout`,
    register: `${auth}register`,
    register_professional: `${auth}register/professional`,
    verify_user: `${auth}jwt/verify_workaround`,
    confirm_email: `${auth}verify`,

    // Geography
    search_state: (query: string) => `${geography}${state}${search_suffix}?q=${encodeURIComponent(query)}`,

    // Search
    search_me: `${search_suffix}${me}`,
    search_patient: (query: string) => `${search_suffix}patient/?query=${encodeURIComponent(query)}`,
    search_patient_name_masked: (query: string) => `${search_suffix}masked_patient/?query=${encodeURIComponent(query)}`,
    check_username: (query: string) => `${user}username/availability${query_suffix}${encodeURIComponent(query)}`,
    update_user_profile: () => `${update_suffix}/${base}`,
    updatePatientInfoByPro: (username: string) => `${update_suffix}/patient/${encodeURIComponent(username)}`,
    updateProInfoByPro: () => `${update_suffix}/${pro}`,
    updateProWorkLocationAndAssignment: (locationId?: string) => 
        locationId 
            ? `${update_suffix}/pro/work-location?location_id=${encodeURIComponent(locationId)}`
            : `${update_suffix}/pro/work-location`,
    // Drugs & Conditions
    search_drugs: (query: string) => `${drugs}${read_suffix}/?query=${encodeURIComponent(query)}`,
    search_conditions: (query: string) => `${cieConditions}${read_suffix}/?query=${encodeURIComponent(query)}`,
    search_medic: (query: string, skip: number = 0, limit: number = 10) => 
        `${search_suffix}${medic}?query=${encodeURIComponent(query)}&skip=${skip}&limit=${limit}`,
    // Health Background Reads
    fetch_own_health_background: `${health_background}${me}`,
    fetch_patient_health_background: (username: string) => `${health_background}${encodeURIComponent(username)}`,

    // Allergies (Professional Mutations)
    add_patient_allergy: (username: string) => 
        `${health_background}${encodeURIComponent(username)}/${allergies}`,
    update_patient_allergy: (username: string, allergyId: string) => 
        `${health_background}${encodeURIComponent(username)}/${allergies}${allergyId}`,
    add_patient_allergy_note: (username: string, allergyId: string) => 
        `${health_background}${encodeURIComponent(username)}/${allergies}${allergyId}${notes}`,
    add_patient_allergy_reaction: (username: string, allergyId: string) => 
        `${health_background}${encodeURIComponent(username)}/${allergies}${allergyId}${reactions}`,
    delete_patient_allergy: (username: string, allergyId: string) => 
        `${health_background}${encodeURIComponent(username)}/${allergies}${allergyId}`,

    // Hereditary (Professional Mutations)
    add_patient_family_member: (username: string) => 
        `${health_background}${encodeURIComponent(username)}/${hereditary}${members}`,
    add_patient_relative_condition: (username: string, memberId: string) => 
        `${health_background}${encodeURIComponent(username)}/${hereditary}${members}${memberId}${conditions}`,
    delete_patient_family_member: (username: string, memberId: string) => 
        `${health_background}${encodeURIComponent(username)}/${hereditary}${members}${memberId}`,
    bulk_update_patient_hereditary: (username: string) => 
    `${health_background}${encodeURIComponent(username)}/${hereditary}`,

    // Whitelist
    read_whitelist: `${whitelist}${read_suffix}`,
    update_whitelist: `${whitelist}${update_suffix}`,
    add_one_whitelist: `${whitelist}${update_suffix}${add_one}`,
    search_user_whitelist: (query: string) => `${whitelist}${search_suffix}?username=${encodeURIComponent(query)}`,

    read_assistants: `${medic_assistant}my-assistants`,
    update_assistants: `${medic_assistant}my-assistants`,
    add_assistant: (username: string) => `${medic_assistant}add-assistant/${encodeURIComponent(username)}`,
    remove_assistant: (username: string) => `${medic_assistant}remove-assistant/${encodeURIComponent(username)}`,
    assigned_medics: `${medic_assistant}assigned-medics`,

    // Onboarding
    onboarding_profile_create: `${user}${base}${create_suffix}`,
    onboarding_pro_create: `${user}${pro}${create_suffix}`,

    onboarding_profile_upgrade: `${user}${base}${upgrade_suffix}`,
    onboarding_pro_upgrade: `${user}${pro}${upgrade_suffix}`,
    
    fetch_addresses_by_id: (query: string) => `user/me/address/read/?user_id=${encodeURIComponent(query)}`,
    fetch_my_addresses: `address/read`,
    update_my_active_addresses: (address_id: string) => `user/me/active_address/update/?address_id=${encodeURIComponent(address_id)}`,

    fetch_address_active: (query: string) => `${address_active}${read_suffix}/?username=${encodeURIComponent(query)}`,
    update_addresses_active: (address_id: string) => `${address_active}${update_suffix}/?address_id=${encodeURIComponent(address_id)}`,
    
    // User & Contact
    get_patient_history: (query: string) => `${diagnoses}${read_suffix}${query_suffix}${encodeURIComponent(query)}`,
    read_full_name: (query: string) => `${search_suffix}${full_name}/?username=${encodeURIComponent(query)}`,
    fetch_user_contact: `${contact}${read_suffix}`,
    update_user_contact: `${contact}${update_suffix}`,

    update_user_allergies: `${user}${allergies}${update_suffix}`,

    fetch_user_general_info: (query: string) => `${general_info}${read_suffix}1${query_suffix}${encodeURIComponent(query)}`,
    update_user_general_info: `${general_info}${update_suffix}/`,

    fetch_health_background: (query?: string) => 
        query ? `${health_background}${read_suffix}/?username=${encodeURIComponent(query)}` : `${health_background}${read_suffix}`,

    update_user_hereditary_info: `${hereditary}${update_suffix}`,
    update_user_prescriptions: `${prescriptions}${update_suffix}/`,

    // Qualifications & Skills
    get_qualifications: (query: string) => `${qualifications}${read_suffix}/?user_id=${encodeURIComponent(query)}`,
    get_qualifications_no_query: () => `${qualifications}${read_suffix}`,
    update_qualifications: `${qualifications}${update_suffix}`,
    fetch_user_skills: `${skills}${read_suffix}/`,
    update_user_skills: `${skills}${update_suffix}`,
    search_skills: (query: string) => `${skills}${search_suffix}?query=${encodeURIComponent(query)}`,

    // Specialties
    category_specialties: (query: string, lan: number = 0) => `${specialties}${read_suffix}?q=${encodeURIComponent(query)}&lan=${lan}`,
    all_specialties: (lan: number = 0) => `${specialties}${all_suffix}/?lan=${lan}`,
    search_specialties: (query: string, limit: number = 20) =>
  `${specialties}${search_suffix}?q=${encodeURIComponent(query)}&limit=${limit}`,

    search_subspecialties: (query: string, specialtyId?: string, limit: number = 20) => {
    const specParam = specialtyId ? `&specialty_id=${encodeURIComponent(specialtyId)}` : '';
    return `${specialties}${subspecialties}${search_suffix}?q=${encodeURIComponent(query)}${specParam}&limit=${limit}`;
    },

    // Subspecialties
    category_subspecialties: (query: string, lan: number = 0) => `${subspecialties}${read_suffix}?q=${encodeURIComponent(query)}&lan=${lan}`,
    all_subspecialties: (lan: number = 0) => `${subspecialties}${all_suffix}/?lan=${lan}`,

    // Address & Business Hours
    fetch_addresses: (query: string) => `${address}${read_suffix}/?username=${encodeURIComponent(query)}`,
    update_addresses: `${address}${update_suffix}`,
    update_business_hours: `${business_hours}${update_suffix}/`,
    
    // Agenda Routes
    create_user_entry: `${agenda}${user}${entries}`,
    create_pro_entry: `${agenda}${pro}${entries}`,
    
    fetch_weekly_schedule: (medic_username: string, year: number, weekNumber: number) => 
        `${agenda}${schedule}${medic_username}?year=${year}&week=${weekNumber}`,

    update_agenda_entry: (entry_id: string) => 
        `${agenda}${entries}${entry_id}`,

    delete_agenda_entry: (entry_id: string) => 
        `${agenda}${entries}${entry_id}`,

    // Diagnoses & Odontogram
    create_diagnosis_entry: `diagnoses/create`,
    fetch_odontogram_history: (query: string) => `${odontogram}${read_suffix}/?patient_username=${encodeURIComponent(query)}`,
    create_odontogram_entry: `${odontogram}create`,
};
  
export default API_ROUTES;